// ═══════════════════════════════════════════════════════════════════════════
// TermsOfService — conditions d'utilisation, FR/EN selon la route
// (/terms ou /en/terms). Alignées sur les vraies fonctionnalités : dispos,
// sondages, crews, chat avec médias, position en direct opt-in, réponses via
// lien sans compte, suppression de compte in-app.
// Mise à jour du contenu = éditer CONTENT ci-dessous + changer `updated`.
// ═══════════════════════════════════════════════════════════════════════════

import LegalPage from "./LegalPage";

const CONTACT = <a href="mailto:contact@getdispo.com">contact@getdispo.com</a>;

const CONTENT = {
  fr: {
    eyebrow: "Dispo",
    title: "Conditions d'utilisation",
    updated: "Dernière mise à jour : 24 août 2026",
    sections: [
      {
        h: "Acceptation",
        p: [
          <>
            En créant un compte ou en utilisant Dispo, tu acceptes ces
            conditions. Si tu n'es pas d'accord avec l'une d'elles, n'utilise
            pas le service.
          </>,
        ],
      },
      {
        h: "Le service",
        p: [<>Dispo te permet notamment de :</>],
        list: [
          <>partager tes disponibilités (« dispos ») avec tes amis ;</>,
          <>lancer des sondages de dates et voter ;</>,
          <>créer des crews (groupes d'amis) ;</>,
          <>
            discuter dans les conversations liées à tes sorties : messages,
            photos, vidéos, messages vocaux, réactions, et partage de position
            en direct ;
          </>,
          <>
            inviter des personnes par lien : elles peuvent voir la dispo et
            répondre depuis leur navigateur, sans installer l'app.
          </>,
        ],
      },
      {
        h: "Ton compte",
        p: [
          <>
            Tu peux créer un compte avec un pseudo et un mot de passe, ou te
            connecter avec Apple ou Google. Tu dois avoir au moins 13 ans. Tu
            es responsable de la confidentialité de tes identifiants et de
            l'activité de ton compte.
          </>,
          <>
            <strong>Important</strong> : l'email est optionnel, mais sans email
            de récupération un compte pseudo/mot de passe ne peut pas être
            récupéré en cas d'oubli. Nous te recommandons d'en ajouter un dans
            ton profil.
          </>,
        ],
      },
      {
        h: "Ton contenu",
        p: [
          <>
            Tu restes propriétaire de ce que tu publies (messages, photos,
            vidéos, dispos…). Tu nous accordes uniquement la licence technique
            nécessaire pour l'héberger et l'afficher aux personnes que tu as
            choisies. Ce contenu doit respecter la loi : pas de contenu
            haineux, harcelant, illégal ou portant atteinte aux droits
            d'autrui.
          </>,
          <>
            Pour signaler un contenu ou un comportement : {CONTACT}. Nous
            pouvons retirer un contenu ou suspendre un compte en cas d'abus.
          </>,
        ],
      },
      {
        h: "Position en direct",
        p: [
          <>
            Le partage de position est toujours à ton initiative, pour une
            durée limitée que tu choisis, et visible uniquement de la
            conversation dans laquelle tu l'actives. Il s'arrête
            automatiquement. Ne l'utilise pas pour suivre quelqu'un sans son
            accord.
          </>,
        ],
      },
      {
        h: "Propriété intellectuelle",
        p: [
          <>
            L'application, son nom, son logo et son design sont protégés. Tu
            ne peux pas les copier, les modifier ou les redistribuer sans
            autorisation écrite.
          </>,
        ],
      },
      {
        h: "Responsabilité",
        p: [
          <>
            Dispo est fourni « en l'état ». Nous faisons de notre mieux pour
            un service fiable et disponible, sans pouvoir le garantir à 100 %.
            Dans les limites permises par la loi, nous ne sommes pas
            responsables des dommages indirects liés à l'utilisation du
            service, ni des contenus publiés par les utilisateurs.
          </>,
        ],
      },
      {
        h: "Résiliation",
        p: [
          <>
            Tu peux supprimer ton compte à tout moment directement dans l'app
            (Paramètres → Compte) ou via{" "}
            <a href="/askdelete">getdispo.com/askdelete</a>. Nous pouvons
            suspendre ou supprimer un compte en cas de violation de ces
            conditions.
          </>,
        ],
      },
      {
        h: "Modifications",
        p: [
          <>
            Nous pouvons faire évoluer ces conditions. En cas de changement
            important, nous te préviendrons dans l'application ; la poursuite
            de l'utilisation vaut acceptation.
          </>,
        ],
      },
      {
        h: "Droit applicable",
        p: [
          <>
            Ces conditions sont régies par le droit français. Tout litige
            relève des tribunaux compétents en France, sans préjudice des
            règles impératives de protection des consommateurs de ton pays de
            résidence.
          </>,
        ],
      },
      {
        h: "Contact",
        p: [<>Une question sur ces conditions ? {CONTACT}.</>],
      },
    ],
  },
  en: {
    eyebrow: "Dispo",
    title: "Terms of Use",
    updated: "Last updated: August 24, 2026",
    sections: [
      {
        h: "Acceptance",
        p: [
          <>
            By creating an account or using Dispo, you agree to these terms.
            If you disagree with any of them, please do not use the service.
          </>,
        ],
      },
      {
        h: "The service",
        p: [<>Dispo lets you, among other things:</>],
        list: [
          <>share when you're free ("dispos") with your friends;</>,
          <>start date polls and vote;</>,
          <>create crews (groups of friends);</>,
          <>
            chat in the conversations tied to your plans: messages, photos,
            videos, voice messages, reactions, and live location sharing;
          </>,
          <>
            invite people with a link: they can view the dispo and reply from
            their browser, without installing the app.
          </>,
        ],
      },
      {
        h: "Your account",
        p: [
          <>
            You can create an account with a username and password, or sign in
            with Apple or Google. You must be at least 13. You are responsible
            for keeping your credentials confidential and for the activity on
            your account.
          </>,
          <>
            <strong>Important</strong>: email is optional, but without a
            recovery email a username/password account cannot be recovered if
            you forget your password. We recommend adding one in your
            profile.
          </>,
        ],
      },
      {
        h: "Your content",
        p: [
          <>
            You keep ownership of what you post (messages, photos, videos,
            dispos…). You grant us only the technical licence needed to host
            it and show it to the people you chose. Your content must comply
            with the law: no hateful, harassing, illegal or infringing
            content.
          </>,
          <>
            To report content or behaviour: {CONTACT}. We may remove content
            or suspend accounts in case of abuse.
          </>,
        ],
      },
      {
        h: "Live location",
        p: [
          <>
            Location sharing is always initiated by you, for a limited
            duration you choose, and visible only to the conversation where
            you enabled it. It stops automatically. Do not use it to track
            anyone without their consent.
          </>,
        ],
      },
      {
        h: "Intellectual property",
        p: [
          <>
            The app, its name, logo and design are protected. You may not
            copy, modify or redistribute them without written permission.
          </>,
        ],
      },
      {
        h: "Liability",
        p: [
          <>
            Dispo is provided "as is". We do our best to keep the service
            reliable and available but cannot guarantee it 100%. To the extent
            permitted by law, we are not liable for indirect damages arising
            from the use of the service, nor for user-generated content.
          </>,
        ],
      },
      {
        h: "Termination",
        p: [
          <>
            You can delete your account at any time directly in the app
            (Settings → Account) or via{" "}
            <a href="/askdelete">getdispo.com/askdelete</a>. We may suspend or
            delete an account that violates these terms.
          </>,
        ],
      },
      {
        h: "Changes",
        p: [
          <>
            We may update these terms. For significant changes we will notify
            you in the app; continued use counts as acceptance.
          </>,
        ],
      },
      {
        h: "Governing law",
        p: [
          <>
            These terms are governed by French law. Disputes fall under the
            competent courts in France, without prejudice to mandatory
            consumer-protection rules of your country of residence.
          </>,
        ],
      },
      {
        h: "Contact",
        p: [<>Questions about these terms? {CONTACT}.</>],
      },
    ],
  },
};

const TermsOfService = ({ lang = "fr" }) => (
  <LegalPage content={CONTENT[lang] || CONTENT.fr} lang={lang} />
);

export default TermsOfService;
